// Was die Bildschirme der Besichtigung gemeinsam brauchen: Daten vom Gerät,
// die sich von selbst aktualisieren, Speichern beim Tippen, der Hinweis
// „alles gesendet“ und der Hinweis für Safari. Seit E2, Schritt 4 auch: der
// Weg zurück zur Startseite (mit Haus) und was die Seiten sagen, wenn eine
// Besichtigung schon gesendet ist.

import { useCallback, useEffect, useRef, useState } from "react";
import { liveQuery } from "dexie";
import { Chip, fehlerMelden, Ikon, toastZeigen, useNetz, type KopfZurueck } from "@happy-energy/oberflaeche";
import { postausgangStand, type PostausgangStand } from "../postausgang.ts";
import { SchonGesendet } from "../arbeitskopie.ts";

/** Oben links zur Startseite: „‹ ⌂ Start“ – überall gleich (Gernot, 19.10.2026). */
export const ZUM_START: KopfZurueck = { adresse: "#/", name: "Start", ikon: "haus" };

/**
 * Speichern ging nicht: Ist die Besichtigung schon gesendet, sagt der Toast
 * das (und die Fehlerwache erfährt nichts – das ist kein Fehler). Sonst
 * `sonst`, und die Fehlerwache bekommt es.
 */
export function speichernFehlgeschlagen(fehler: unknown, sonst = "Konnte nicht speichern – bitte noch einmal tippen."): void {
  if (fehler instanceof SchonGesendet) {
    toastZeigen(fehler.message, "fehler");
    return;
  }
  fehlerMelden("doku", fehler);
  toastZeigen(sonst, "fehler");
}

/**
 * Eine Abfrage an den Speicher am Gerät, die sich selbst neu liest, sobald
 * sich dort etwas ändert. Solange sie noch nicht da ist: undefined.
 * `schluessel` sagt, wann die Abfrage eine andere ist (z. B. die Kennung).
 */
export function useLive<T>(abfrage: () => Promise<T>, schluessel: string): T | undefined {
  const [stand, setStand] = useState<{ schluessel: string; wert: T } | undefined>(undefined);
  const abfrageAktuell = useRef(abfrage);
  useEffect(() => {
    abfrageAktuell.current = abfrage;
  });
  useEffect(() => {
    const abo = liveQuery(() => abfrageAktuell.current()).subscribe({
      next: (wert) => setStand({ schluessel, wert }),
      error: (fehler: unknown) => fehlerMelden("doku", fehler),
    });
    return () => abo.unsubscribe();
  }, [schluessel]);
  // Gehört der Stand noch zur alten Abfrage, gilt er nicht.
  return stand?.schluessel === schluessel ? stand.wert : undefined;
}

/** Was im Postausgang wartet – für den Hinweis oben und die Listen. */
export function usePostausgang(): PostausgangStand {
  return useLive(postausgangStand, "postausgang") ?? { wartend: 0, jeAufnahme: {} };
}

/**
 * Speichern beim Tippen, entprellt: Erst wenn eine kurze Pause ist, geht es
 * in den Speicher (und in den Postausgang). Beim Verlassen des Feldes, der
 * Seite oder wenn die App in den Hintergrund geht, sofort – so geht auch
 * das letzte Wort nicht verloren. `verwerfen` vergisst, was noch offen ist
 * (etwa, wenn das Foto gerade gelöscht wird).
 */
export function useSpeichern<T>(speichern: (wert: T) => Promise<void>, ms = 600) {
  const offen = useRef<{ wert: T; uhr: number } | null>(null);
  const speichernAktuell = useRef(speichern);
  useEffect(() => {
    speichernAktuell.current = speichern;
  });

  const sofort = useCallback(() => {
    const o = offen.current;
    if (!o) return;
    window.clearTimeout(o.uhr);
    offen.current = null;
    speichernAktuell.current(o.wert).catch((fehler: unknown) => speichernFehlgeschlagen(fehler));
  }, []);

  const verwerfen = useCallback(() => {
    if (offen.current) window.clearTimeout(offen.current.uhr);
    offen.current = null;
  }, []);

  const spaeter = useCallback(
    (wert: T) => {
      if (offen.current) window.clearTimeout(offen.current.uhr);
      offen.current = { wert, uhr: window.setTimeout(sofort, ms) };
    },
    [ms, sofort],
  );

  useEffect(() => {
    const hintergrund = () => {
      if (document.visibilityState === "hidden") sofort();
    };
    document.addEventListener("visibilitychange", hintergrund);
    window.addEventListener("pagehide", sofort);
    return () => {
      document.removeEventListener("visibilitychange", hintergrund);
      window.removeEventListener("pagehide", sofort);
      sofort();
    };
  }, [sofort]);

  return { spaeter, sofort, verwerfen };
}

/**
 * Ein Text nur zum Lesen (gesendet, seit Schritt 4): so, wie getippt, mit
 * seinen Zeilenumbrüchen. Leer steht `leer` da, in Grau – nie ein leeres Feld,
 * das zum Tippen einlädt.
 */
export function LeseText({ text, leer }: { text: string | null | undefined; leer: string }) {
  const da = text !== null && text !== undefined && text.trim() !== "";
  return <p className={`lese-text${da ? "" : " leer"}`}>{da ? text : leer}</p>;
}

/** „gespeichert“ neben dem Feld, das zuletzt gespeichert wurde – damit man sieht, dass es am Gerät ist. */
export function Gespeichert({ an }: { an: boolean }) {
  if (!an) return null;
  return (
    <span className="gespeichert">
      <Ikon name="haken" groesse="k" /> gespeichert
    </span>
  );
}

/**
 * Oben rechts: „alles gesendet“, „3 wartet“ oder „ohne Netz – 3 wartet“.
 * Auf dem Handy die kurze Form („gesendet“, „3 wartet“ mit dem Zeichen für
 * „kein Netz“), damit der Titel in der Mitte bleibt.
 */
export function SendeStand({ wartend }: { wartend: number }) {
  const netz = useNetz();
  if (!netz) {
    return (
      <Chip art="dunkel" ikon="offline">
        {wartend > 0 ? (
          // Eine Hülle um den Text: Im Chip (Flexbox) wäre jedes Stück sonst ein eigenes Kästchen.
          <span>
            <span className="nur-breit">ohne Netz – </span>
            {wartend} wartet
          </span>
        ) : (
          "ohne Netz"
        )}
      </Chip>
    );
  }
  if (wartend > 0) {
    return (
      <Chip art="dunkel" ikon="uhr">
        {wartend} wartet
      </Chip>
    );
  }
  return (
    <Chip art="dunkel" ikon="wolke">
      <span>
        <span className="nur-breit">alles </span>gesendet
      </span>
    </Chip>
  );
}

/** Ein Fehler, weil am Gerät kein Platz mehr ist (IndexedDB voll) – beim Foto und beim K2-PDF. */
export function keinPlatz(fehler: unknown): boolean {
  const f = fehler as { name?: string; inner?: { name?: string } } | null;
  return f?.name === "QuotaExceededError" || f?.inner?.name === "QuotaExceededError";
}

/** „1 Foto“ oder „3 Fotos“: `eins` bei genau einem, sonst `viele` mit # als Zahl – eine Regel für alle Zählungen. */
export function anzahl(n: number, eins: string, viele: string): string {
  return n === 1 ? eins : viele.replace("#", String(n));
}

/**
 * Zwei Buchstaben für den runden Kasten in der Liste: „Frau Maierhofer“ →
 * „MA“. Seit E3b (O81 c, Code-Prüfung zweites Update, Befund 2) dieselbe
 * Rechnung wie im CRM: nur Buchstaben, keine Zahlen – „Haus 12“ → „HA“,
 * „Familie Test-12ab“ → „TE“.
 */
export function kuerzel(name: string): string {
  const BUCHSTABE = /\p{L}/u;
  const woerter = name
    .trim()
    .split(/\s+/)
    .filter((w) => BUCHSTABE.test(w));
  const ohneAnrede = woerter.filter((w) => !/^(frau|herr|familie|fam\.?|dr\.?|ing\.?|mag\.?|di)$/i.test(w));
  const wort = ohneAnrede[ohneAnrede.length - 1] ?? woerter[woerter.length - 1];
  if (!wort) return "?";
  return [...wort]
    .filter((z) => BUCHSTABE.test(z))
    .slice(0, 2)
    .join("")
    .toLocaleUpperCase("de-AT");
}

/** Läuft die App am iPhone in Safari statt vom Home-Bildschirm? Dann hat sie dort einen eigenen Speicher. */
export function inSafariStattApp(): boolean {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const installiert = window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios && !installiert;
}
